import { BIBLE_BOOKS } from '../../../shared/types';
import { calcProgressPct, getTileSize, getTileClass } from '../utils/progressUtils';

interface ChapterProgressGridProps {
  book: string;
  /** Set of chapter numbers already completed historically (faded green) */
  completedChapters: Set<number>;
  /** Optional read-through number — only shown if > 1 */
  readThrough?: number;
  /**
   * When provided, the grid is interactive. Tiles in this set are shown as
   * bright green ("newly selected"). Clicking any tile calls onToggle.
   */
  selectedChapters?: Set<number>;
  onToggle?: (chapter: number) => void;
}

const ChapterProgressGrid = ({
  book,
  completedChapters,
  readThrough,
  selectedChapters,
  onToggle,
}: ChapterProgressGridProps) => {
  const bibleBook = BIBLE_BOOKS.find((b) => b.name === book);
  if (!bibleBook) return null;

  const totalChapters = bibleBook.chapters;
  const isInteractive = typeof onToggle === 'function';

  // In display mode: count completed. In interactive mode: count selected.
  const displayCount = isInteractive
    ? (selectedChapters?.size ?? 0)
    : completedChapters.size;
  const pct = calcProgressPct(displayCount, totalChapters);

  // Use smaller tiles for books with many chapters
  const tileSize = getTileSize(totalChapters);

  return (
    <div className="flex flex-col gap-3">
      {/* Heading row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-800">{book}</span>
          {readThrough && readThrough > 1 && (
            <span className="text-xs font-medium text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full">
              Read-through #{readThrough}
            </span>
          )}
        </div>
        <span className="text-xs text-gray-500 font-medium">
          {displayCount}/{totalChapters} · {pct}%
        </span>
      </div>

      {isInteractive && (
        <p className="text-xs text-gray-400 -mt-1">Tap chapters you read this session</p>
      )}

      {/* Chapter tiles */}
      <div className="pr-1">
        <div className="flex flex-wrap gap-1.5">
          {Array.from({ length: totalChapters }, (_, i) => i + 1).map((ch) => {
            const isSelected = selectedChapters?.has(ch) ?? false;
            const isPrior = completedChapters.has(ch);

            const tileClass = getTileClass(isInteractive, isSelected, isPrior);

            const label = `Chapter ${ch}${isSelected ? ' — selected' : isPrior ? ' — completed' : ''}`;

            if (isInteractive) {
              return (
                <button
                  key={ch}
                  type="button"
                  title={label}
                  onClick={() => onToggle!(ch)}
                  className={`
                    ${tileSize} rounded-md flex items-center justify-center font-semibold
                    transition-all duration-100 select-none active:scale-90 cursor-pointer
                    ${tileClass}
                  `}
                >
                  {ch}
                </button>
              );
            }

            return (
              <div
                key={ch}
                title={label}
                className={`
                  ${tileSize} rounded-md flex items-center justify-center font-semibold
                  transition-colors duration-150 select-none
                  ${tileClass}
                `}
              >
                {ch}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ChapterProgressGrid;
